import events from '~/modules/game-events/game-events';
import SongsService from '~/modules/songs/songs-service';
import { getAllStats } from '~/modules/songs/stats/common';
import { Achievement, achievementsUnlockedByLatestPlay, SongLookup } from '~/modules/user-profiles/achievements';
import { backfillProfilesFromPlays } from '~/modules/user-profiles/backfill-from-plays';
import { playsForName, summarisePlays } from '~/modules/user-profiles/profile-stats';
import UserProfiles, { normaliseProfileName } from '~/modules/user-profiles/profiles';
import isPreRendering from '~/modules/utils/is-pre-rendering';

/** Genre and language, keyed the way plays are - see `use-songs-by-key` for the same map in React. */
const songLookup = (): SongLookup =>
  Object.fromEntries(
    (SongsService.getCurrentIndex() ?? []).map((song) => [song.id, { genre: song.genre, language: song.language }]),
  );

const announce = async (profileName: string, achievements: Achievement[]) => {
  const { toast } = await import('react-toastify');

  for (const achievement of achievements) {
    toast.success(
      <span>
        <b className="ph-no-capture">{profileName}</b> unlocked {achievement.icon} <b>{achievement.name}</b>
        <br />
        <span className="text-base">{achievement.description}</span>
      </span>,
      // Keyed per profile and achievement, so re-entering the results screen cannot double up.
      { toastId: `achievement-${profileName}-${achievement.id}`, autoClose: 6000 },
    );
  }
};

/**
 * Announces achievements the song that just finished was the one to unlock. Runs off
 * `songStatStored` rather than `songEnded`, so the play it reasons about is already in the store
 * it reads back.
 */
events.songStatStored.subscribe(async (_songKey, songStats) => {
  if (isPreRendering) return;

  const profiles = UserProfiles.getAll();
  if (profiles.length === 0) return;

  // Only the singers of the round just stored: every other profile's newest play is an old one,
  // and asking what that unlocked would announce the same achievement after every song.
  const justSang = new Set((songStats.scores.at(-1)?.scores ?? []).map((entry) => normaliseProfileName(entry.name)));
  const sangThisRound = profiles.filter((profile) => justSang.has(normaliseProfileName(profile.name)));
  if (sangThisRound.length === 0) return;

  const [stats, songs] = [await getAllStats(), songLookup()];

  for (const profile of sangThisRound) {
    const unlocked = achievementsUnlockedByLatestPlay(summarisePlays(playsForName(profile.name, stats)), songs);

    if (unlocked.length > 0) {
      await announce(profile.name, unlocked);
    }
  }
});

if (!isPreRendering) {
  void backfillProfilesFromPlays().catch((error) => console.warn('Could not backfill profiles from plays', error));
}
